import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context';
import api, { secureFetch } from '../api/client';
import Turnstile, { TURNSTILE_SITE_KEY } from './Turnstile';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';


const ROLES = [
  { key: 'user',    icon: '🧙', nameKey: 'auth.role_user',    subKey: 'auth.role_user_sub' },
  { key: 'partner', icon: '🏰', nameKey: 'auth.role_partner', subKey: 'auth.role_partner_sub' },
  { key: 'owner',   icon: '⚔️', nameKey: 'auth.role_owner',   subKey: 'auth.role_owner_sub' },
];

const COUNTRY_OPTS = [
  { code: 'LT', name: 'Lietuva' },   { code: 'LV', name: 'Latvija' },
  { code: 'EE', name: 'Estija' },    { code: 'PL', name: 'Lenkija' },
  { code: 'DE', name: 'Vokietija' }, { code: 'UK', name: 'Jungtinė Karalystė' },
  { code: 'FR', name: 'Prancūzija'}, { code: 'ES', name: 'Ispanija' },
  { code: 'IT', name: 'Italija' },   { code: 'NL', name: 'Nyderlandai' },
  { code: 'BE', name: 'Belgija' },   { code: 'CH', name: 'Šveicarija' },
  { code: 'AT', name: 'Austrija' },  { code: 'CZ', name: 'Čekija' },
  { code: 'FI', name: 'Suomija' },   { code: 'SE', name: 'Švedija' },
  { code: 'DK', name: 'Danija' },    { code: 'NO', name: 'Norvegija' },
  { code: 'UA', name: 'Ukraina' },   { code: 'US', name: 'JAV' },
  { code: 'CA', name: 'Kanada' },    { code: 'AU', name: 'Australija' },
  { code: 'JP', name: 'Japonija' },
];

// ── Password strength checker ─────────────────────────────────────────────────
function pwStrength(pw) {
  const checks = {
    length:  pw.length >= 15,
    upper:   /[A-Z]/.test(pw),
    lower:   /[a-z]/.test(pw),
    digit:   /[0-9]/.test(pw),
    special: /[^A-Za-z0-9]/.test(pw),
  };
  const score = Object.values(checks).filter(Boolean).length;
  return { checks, score, valid: score === 5 };
}

const PW_LABELS = {
  length:  'Min. 15 simbolių',
  upper:   'Didžioji raidė (A-Z)',
  lower:   'Mažoji raidė (a-z)',
  digit:   'Skaičius (0-9)',
  special: 'Specialus simbolis (!@#$...)',
};

const PW_BAR_COLOR = ['#cc4444', '#cc7722', '#aaaa22', '#44aa66', '#2288cc'];

function PasswordStrength({ password }) {
  const { checks, score } = pwStrength(password);
  if (!password) return null;
  return (
    <div style={{ marginTop: 8 }}>
      {/* bar */}
      <div style={{ display: 'flex', gap: 3, marginBottom: 8 }}>
        {[0,1,2,3,4].map(i => (
          <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i < score ? PW_BAR_COLOR[score - 1] : 'rgba(255,255,255,0.1)', transition: 'background .2s' }} />
        ))}
      </div>
      {/* checks */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 10px' }}>
        {Object.entries(PW_LABELS).map(([k, label]) => (
          <div key={k} style={{ fontSize: 11, color: checks[k] ? '#50e080' : 'rgba(200,180,255,0.4)', display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ fontSize: 10 }}>{checks[k] ? '✓' : '○'}</span>
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Etapai: 'form' | 'otp' | 'verify_sent' ───────────────────────────────────
// asPage=true – komponentas rodomas kaip pilnas puslapis (pvz. /register), ne popup.
export default function AuthModal({ onClose, initialTab = 'login', asPage = false }) {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();

  // Popup režime registracija atidaroma kaip atskiras puslapis /register
  const goRegister = () => {
    if (asPage) { setTab('register'); reset(); }
    else { onClose(); navigate('/register'); }
  };

  const [tab,  setTab]  = useState(initialTab);
  const [step, setStep] = useState('form');
  const [role, setRole] = useState('user');
  const [showPw,  setShowPw]  = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  const [form,  setForm] = useState({
    name: '', email: '', password: '', confirm: '',
    country: '', city: '', birth_year: '',
  });
  const [otp,   setOtp]   = useState('');
  const [otpId, setOtpId] = useState(null);

  // Consents
  const [gdpr,  setGdpr]  = useState(false);
  const [aiCon, setAiCon] = useState(false);
  const [age13, setAge13] = useState(false);
  const [scrolledToConsent, setScrolledToConsent] = useState(asPage);
  const formScrollRef = useRef(null);
  const consentRef    = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [info,    setInfo]    = useState('');

  // Cloudflare Turnstile
  const [captcha, setCaptcha] = useState('');
  const captchaResetRef = useRef(null);
  const captchaOk = !TURNSTILE_SITE_KEY || !!captcha;

  const setF = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const reset = () => { setStep('form'); setError(''); setInfo(''); setOtp(''); setOtpId(null); setCaptcha(''); };

  // ── Socialiniai prisijungimai ─────────────────────────────────────────────
  const [providers, setProviders] = useState(null);

  useEffect(() => {
    api.get('/auth/social/providers')
      .then(r => setProviders(r.data))
      .catch(() => setProviders(null));
  }, []);

  const googleClientId = (providers?.google?.enabled && providers.google.client_id) || GOOGLE_CLIENT_ID;

  const oauthStart = async (p) => {
    setError('');
    try {
      const r = await api.get(`/auth/oauth/${p}/start`);
      window.location.href = r.data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || `${p} prisijungimas nesukonfigūruotas`);
    }
  };

  // ── Google Sign-In ────────────────────────────────────────────────────────
  const googleBtnRef = useRef(null);

  const handleGoogleCredential = useCallback(async (response) => {
    setError(''); setLoading(true);
    try {
      const r = await api.post('/auth/google', { credential: response.credential });
      const d = r.data;
      login({ id: d.user.id, name: d.user.name, email: d.user.email, role: d.user.role, token: d.access_token, csrf_token: d.csrf_token });
      onClose();
    } catch(e) {
      setError(e?.response?.data?.detail || 'Google prisijungimas nepavyko');
    } finally { setLoading(false); }
  }, [login, onClose]); // eslint-disable-line

  useEffect(() => {
    if (!googleClientId) return;
    const renderGoogle = () => {
      if (!window.google || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleGoogleCredential,
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'filled_black', size: 'large', shape: 'pill',
        text: 'signin_with', locale: 'lt',
        width: googleBtnRef.current.offsetWidth || 340,
      });
    };
    if (window.google) { renderGoogle(); return; }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = renderGoogle;
    document.head.appendChild(script);
    return () => { try { document.head.removeChild(script); } catch {} };
  }, [handleGoogleCredential, googleClientId, tab]);

  // Detect when user scrolls near the consent section
  useEffect(() => {
    const el = formScrollRef.current;
    if (!el) return;
    const check = () => {
      if (!consentRef.current) return;
      const rect = consentRef.current.getBoundingClientRect();
      const wrapRect = el.getBoundingClientRect();
      if (rect.top < wrapRect.bottom + 40) setScrolledToConsent(true);
    };
    el.addEventListener('scroll', check);
    return () => el.removeEventListener('scroll', check);
  }, [step, tab]);

  const strength = pwStrength(form.password);
  const allConsents = gdpr && aiCon && age13;
  const canRegister = form.name.trim() && form.email && strength.valid &&
    form.password === form.confirm && allConsents && captchaOk;

  // ── Registracija ──────────────────────────────────────────────────────────
  const handleRegister = async () => {
    if (!canRegister) return;
    setError(''); setLoading(true);
    const yearNow = new Date().getFullYear();
    const birth_year = form.birth_year ? parseInt(form.birth_year) : null;
    if (birth_year && (yearNow - birth_year < 13)) {
      setError('Minimalus amžius yra 13 metų'); setLoading(false); return;
    }
    try {
      const res = await secureFetch('/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name, email: form.email, password: form.password,
          role, country: form.country, city: form.city, birth_year,
          turnstile_token: captcha,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Registracija nepavyko');
      setStep('verify_sent');
      setInfo(`✉️ Patvirtinimo laiškas išsiųstas į ${form.email}`);
    } catch (e) {
      setError(e.message);
      captchaResetRef.current?.();
    } finally {
      setLoading(false);
    }
  };

  // ── Prisijungimas ─────────────────────────────────────────────────────────
  const handleLogin = async () => {
    setError(''); setLoading(true);
    try {
      const body = new URLSearchParams({ username: form.email, password: form.password, turnstile_token: captcha });
      const res  = await secureFetch('/auth/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      let data;
      try { data = await res.json(); } catch { throw new Error(res.status >= 500 ? 'Serverio klaida. Bandykite vėliau.' : 'Atsakymo klaida.'); }
      if (!res.ok) throw new Error(data.detail || 'Prisijungimas nepavyko');

      if (data.mfa_required) {
        setOtpId(data.otp_id);
        setInfo(data.detail || 'OTP kodas išsiųstas el. paštu');
        setStep('otp');
      } else {
        login({ id: data.id, name: data.name, email: form.email, role: data.role, token: data.access_token, csrf_token: data.csrf_token });
        onClose();
      }
    } catch (e) {
      setError(e.message);
      captchaResetRef.current?.();
    } finally {
      setLoading(false);
    }
  };

  // ── OTP ──────────────────────────────────────────────────────────────────
  const handleOtp = async () => {
    setError(''); setLoading(true);
    try {
      const res  = await secureFetch('/auth/otp/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ otp_id: otpId, otp_code: otp.trim() }),
      });
      let data;
      try { data = await res.json(); } catch { throw new Error('Serverio klaida. Bandykite vėliau.'); }
      if (!res.ok) throw new Error(data.detail || 'OTP klaida');
      login({ id: data.id, name: data.name, email: form.email, role: data.role, token: data.access_token, csrf_token: data.csrf_token });
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError(''); setLoading(true);
    try {
      const res  = await secureFetch(`/auth/otp/resend?otp_id=${encodeURIComponent(otpId)}`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Nepavyko siųsti');
      setOtpId(data.otp_id);
      setInfo('✉️ Naujas kodas išsiųstas');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const inp = {
    width: '100%', background: 'var(--bg2)', border: '1px solid var(--border)',
    borderRadius: 8, color: '#fff', padding: '9px 12px', fontSize: 13,
    outline: 'none', boxSizing: 'border-box',
  };

  // ── Socialinių mygtukų blokas (login + register) ──────────────────────────
  const anySocial = !!googleClientId || providers?.facebook?.enabled ||
                    providers?.x?.enabled || providers?.linkedin?.enabled;

  const socialBtnStyle = (bg, fg = '#fff') => ({
    width: '100%', background: bg, border: 'none', color: fg,
    borderRadius: 22, padding: '10px 16px', fontSize: 13, fontWeight: 700,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  });

  const socialSection = anySocial && (
    <>
      <div style={{ display:'flex', alignItems:'center', gap:10, margin:'16px 0 12px' }}>
        <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.1)' }} />
        <span style={{ fontSize:11, color:'rgba(200,180,255,0.35)', letterSpacing:1, textTransform:'uppercase' }}>arba</span>
        <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.1)' }} />
      </div>
      {googleClientId && <div ref={googleBtnRef} style={{ minHeight:44, display:'flex', justifyContent:'center', marginBottom:8 }} />}
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {providers?.facebook?.enabled && (
          <button type="button" onClick={() => oauthStart('facebook')} style={socialBtnStyle('#1877f2')}>
            <span style={{ fontSize:16 }}>📘</span> Tęsti su Facebook
          </button>
        )}
        {providers?.x?.enabled && (
          <button type="button" onClick={() => oauthStart('x')} style={socialBtnStyle('#15181c')}>
            <span style={{ fontSize:16, fontWeight:900 }}>𝕏</span> Tęsti su X
          </button>
        )}
        {providers?.linkedin?.enabled && (
          <button type="button" onClick={() => oauthStart('linkedin')} style={socialBtnStyle('#0a66c2')}>
            <span style={{ fontSize:16 }}>💼</span> Tęsti su LinkedIn
          </button>
        )}
      </div>
    </>
  );
  const lbl = { display: 'block', fontSize: 11, color: 'rgba(200,180,255,0.55)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 5 };

  // ── Render ────────────────────────────────────────────────────────────────
  const card = (
      <div className="modal" style={{ maxWidth: asPage ? 560 : 500 }}>
        <div className="modal-header">
          <span className="modal-title">
            {step === 'otp'         ? '🔐 Patvirtinimas' :
             step === 'verify_sent' ? '✉️ Patikrinkite paštą' :
             `⚔️ ${tab === 'login' ? t('auth.login') : t('auth.register')}`}
          </span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body" ref={formScrollRef} style={{ ...(asPage ? {} : { maxHeight: '78vh' }), overflowY: 'auto', padding: '0 0 4px' }}>

          {error && <div className="auth-msg auth-error-msg" style={{ margin: '0 24px 12px' }}>⚠️ {error}</div>}
          {info  && !error && <div className="auth-msg auth-info-msg" style={{ margin: '0 24px 12px' }}>{info}</div>}

          {/* ══ LOGIN form ═══════════════════════════════════════════════ */}
          {step === 'form' && tab === 'login' && (
            <div style={{ padding: '0 24px 24px' }}>
              <div className="tab-pills" style={{ marginBottom: 20 }}>
                <button className="tab-pill active" onClick={() => { setTab('login'); reset(); }}>{t('auth.login')}</button>
                <button className="tab-pill" onClick={goRegister}>{t('auth.register')}</button>
              </div>

              <div className="form-group">
                <label className="form-label">{t('auth.email')}</label>
                <input className="form-input" type="email" placeholder="vartotojas@example.com"
                       value={form.email} onChange={setF('email')}
                       onKeyDown={e => e.key === 'Enter' && handleLogin()} />
              </div>

              <div className="form-group">
                <label className="form-label">{t('auth.password')}</label>
                <div style={{ position: 'relative' }}>
                  <input className="form-input" type={showPw ? 'text' : 'password'} placeholder="••••••••"
                         value={form.password} onChange={setF('password')}
                         onKeyDown={e => e.key === 'Enter' && handleLogin()}
                         style={{ paddingRight: 44 }} />
                  <button type="button" onClick={() => setShowPw(v => !v)}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'rgba(200,180,255,0.5)' }}>
                    {showPw ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <Turnstile onToken={setCaptcha} resetRef={captchaResetRef} />

              <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }}
                onClick={handleLogin} disabled={loading || !form.email || !form.password || !captchaOk}>
                {loading ? '⏳ Kraunama...' : t('auth.login_btn')}
              </button>

              <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text3)', marginTop: 10 }}>
                🔐 Prisijungus bus išsiųstas patvirtinimo kodas el. paštu
              </p>

              {socialSection}
            </div>
          )}

          {/* ══ REGISTER form ════════════════════════════════════════════ */}
          {step === 'form' && tab === 'register' && (
            <div style={{ padding: '0 24px 24px' }}>
              <div className="tab-pills" style={{ marginBottom: 20 }}>
                <button className="tab-pill" onClick={() => { setTab('login'); reset(); }}>{t('auth.login')}</button>
                <button className="tab-pill active" onClick={() => { setTab('register'); reset(); }}>{t('auth.register')}</button>
              </div>

              {/* Role selection */}
              <div className="form-group">
                <label className="form-label">{t('auth.role')}</label>
                <div className="auth-role-grid">
                  {ROLES.map(r => (
                    <div key={r.key} onClick={() => setRole(r.key)} className="auth-role-option" style={{
                      background: role === r.key ? 'rgba(212,160,23,0.1)' : 'var(--bg2)',
                      border: `2px solid ${role === r.key ? 'var(--gold2)' : 'var(--border)'}`,
                      cursor: 'pointer',
                    }}>
                      <div className="auth-role-icon">{r.icon}</div>
                      <div className="auth-role-name">{t(r.nameKey)}</div>
                      <div className="auth-role-sub">{t(r.subKey)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Name */}
              <div className="form-group">
                <label className="form-label">{t('auth.name')} ir Pavardė</label>
                <input className="form-input" type="text" placeholder="Vardas Pavardė"
                       value={form.name} onChange={setF('name')} />
              </div>

              {/* Email */}
              <div className="form-group">
                <label className="form-label">{t('auth.email')}</label>
                <input className="form-input" type="email" placeholder="vartotojas@example.com"
                       value={form.email} onChange={setF('email')} />
              </div>

              {/* Password */}
              <div className="form-group">
                <label className="form-label">{t('auth.password')}</label>
                <div style={{ position: 'relative' }}>
                  <input className="form-input" type={showPw ? 'text' : 'password'}
                         placeholder="Min. 15 simbolių"
                         value={form.password} onChange={setF('password')}
                         style={{ paddingRight: 44 }} />
                  <button type="button" onClick={() => setShowPw(v => !v)}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'rgba(200,180,255,0.5)' }}>
                    {showPw ? '🙈' : '👁️'}
                  </button>
                </div>
                <PasswordStrength password={form.password} />
              </div>

              {/* Confirm password */}
              <div className="form-group">
                <label className="form-label">Pakartoti slaptažodį</label>
                <div style={{ position: 'relative' }}>
                  <input className="form-input" type={showPw2 ? 'text' : 'password'}
                         placeholder="Pakartokite slaptažodį"
                         value={form.confirm} onChange={setF('confirm')}
                         style={{ paddingRight: 44, borderColor: form.confirm && form.confirm !== form.password ? 'rgba(220,60,60,0.6)' : undefined }} />
                  <button type="button" onClick={() => setShowPw2(v => !v)}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'rgba(200,180,255,0.5)' }}>
                    {showPw2 ? '🙈' : '👁️'}
                  </button>
                </div>
                {form.confirm && form.confirm !== form.password && (
                  <div style={{ fontSize: 11, color: '#ff7070', marginTop: 4 }}>⚠ Slaptažodžiai nesutampa</div>
                )}
                {form.confirm && form.confirm === form.password && form.password && (
                  <div style={{ fontSize: 11, color: '#50e080', marginTop: 4 }}>✓ Slaptažodžiai sutampa</div>
                )}
              </div>

              {/* Country + City */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={lbl}>Šalis</label>
                  <select style={{ ...inp, cursor: 'pointer', fontFamily: 'inherit' }} value={form.country} onChange={setF('country')}>
                    <option value="">— Pasirinkti —</option>
                    {COUNTRY_OPTS.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={lbl}>Miestas</label>
                  <input style={inp} type="text" placeholder="Vilnius" value={form.city} onChange={setF('city')} />
                </div>
              </div>

              {/* Age */}
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Gimimo metai (amžiaus patvirtinimui)</label>
                <input className="form-input" type="number" placeholder={`${new Date().getFullYear() - 20}`}
                       min="1900" max={new Date().getFullYear() - 13}
                       value={form.birth_year} onChange={setF('birth_year')} />
              </div>

              {/* ── Sutikimai — reikia slinkti ── */}
              <div ref={consentRef} style={{
                marginTop: 20, padding: 16, borderRadius: 10,
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.07)',
              }}>
                <div style={{ fontSize: 11, color: 'rgba(240,192,64,0.6)', letterSpacing: 2, textTransform: 'uppercase', fontFamily: 'Cinzel,serif', marginBottom: 14 }}>
                  ✦ Sutikimai
                </div>

                {[
                  {
                    id: 'gdpr', val: gdpr, set: setGdpr,
                    label: 'GDPR ir privatumo politika',
                    desc: 'Sutinku su Fantastai.lt privatumo politika ir duomenų tvarkymu pagal BDAR reglamentą.',
                  },
                  {
                    id: 'ai', val: aiCon, set: setAiCon,
                    label: 'AI turinys',
                    desc: 'Sutinku, kad mano profilio duomenys (be jautrių duomenų) gali būti naudojami platformos rekomendacijų gerinimui.',
                  },
                  {
                    id: 'age', val: age13, set: setAge13,
                    label: 'Amžiaus reikalavimas',
                    desc: 'Patvirtinu, kad esu vyresnis(-ė) nei 13 metų ir turiu teisę registruotis šioje platformoje.',
                  },
                ].map(c => (
                  <label key={c.id} style={{ display: 'flex', gap: 12, marginBottom: 14, cursor: scrolledToConsent ? 'pointer' : 'not-allowed', opacity: scrolledToConsent ? 1 : 0.45, alignItems: 'flex-start' }}>
                    <div
                      onClick={() => scrolledToConsent && c.set(v => !v)}
                      style={{
                        width: 20, height: 20, borderRadius: 4, flexShrink: 0, marginTop: 1,
                        background: c.val ? 'linear-gradient(135deg,#5a3e8c,#7b5cbf)' : 'rgba(255,255,255,0.06)',
                        border: `2px solid ${c.val ? '#7b5cbf' : 'rgba(255,255,255,0.15)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all .15s', cursor: scrolledToConsent ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {c.val && <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>✓</span>}
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: '#ede8f8', fontWeight: 600, marginBottom: 2 }}>{c.label}</div>
                      <div style={{ fontSize: 11, color: 'rgba(200,180,255,0.45)', lineHeight: 1.5 }}>{c.desc}</div>
                    </div>
                  </label>
                ))}

                {!scrolledToConsent && (
                  <div style={{ textAlign: 'center', fontSize: 11, color: 'rgba(240,192,64,0.5)', marginTop: 4 }}>
                    ↓ Slinkite žemyn norėdami matyti sutikimus
                  </div>
                )}
              </div>

              <Turnstile onToken={setCaptcha} resetRef={captchaResetRef} />

              <button
                className="btn btn-primary"
                style={{ width: '100%', marginTop: 16, opacity: canRegister ? 1 : 0.45 }}
                onClick={handleRegister}
                disabled={loading || !canRegister}
              >
                {loading ? '⏳ Registruojama...' : !allConsents ? '🔒 Patvirtinkite sutikimus' : !captchaOk ? '🔒 Patvirtinkite, kad nesate robotas' : t('auth.register_btn')}
              </button>

              {socialSection}
            </div>
          )}

          {/* ══ OTP ══════════════════════════════════════════════════════ */}
          {step === 'otp' && (
            <div style={{ padding: '0 24px 24px' }}>
              <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
                Patvirtinimo kodas išsiųstas į<br/>
                <strong style={{ color: 'var(--gold2)' }}>{form.email}</strong>
              </p>
              <div className="form-group">
                <label className="form-label">6 skaitmenų kodas</label>
                <input
                  className="form-input"
                  type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                  placeholder="• • • • • •" value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onKeyDown={e => e.key === 'Enter' && otp.length === 6 && handleOtp()}
                  style={{ fontSize: 24, letterSpacing: 8, textAlign: 'center', fontFamily: 'monospace' }}
                  autoFocus
                />
              </div>
              <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }}
                onClick={handleOtp} disabled={loading || otp.length !== 6}>
                {loading ? '⏳ Tikrinama...' : '✅ Patvirtinti'}
              </button>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
                <button onClick={reset} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 13 }}>← Grįžti</button>
                <button onClick={handleResendOtp} disabled={loading} style={{ background: 'none', border: 'none', color: 'var(--gold2)', cursor: 'pointer', fontSize: 13 }}>📨 Siųsti iš naujo</button>
              </div>
            </div>
          )}

          {/* ══ verify_sent ══════════════════════════════════════════════ */}
          {step === 'verify_sent' && (
            <div style={{ textAlign: 'center', padding: '20px 24px 32px' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>✉️</div>
              <p style={{ color: 'var(--text)', fontSize: 15, lineHeight: 1.7 }}>
                Registracija sėkminga!<br/>
                <strong style={{ color: 'var(--gold2)' }}>{form.email}</strong><br/>
                adresu išsiuntėme patvirtinimo nuorodą.<br/>
                <span style={{ color: 'var(--text3)', fontSize: 13 }}>Nuoroda galioja 24 valandas.</span>
              </p>
              <button className="btn btn-secondary" style={{ marginTop: 16 }} onClick={() => { setTab('login'); reset(); }}>
                Prisijungti
              </button>
            </div>
          )}

        </div>
      </div>
  );

  if (asPage) {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '32px 20px 48px' }}>
        {card}
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && onClose()}>
      {card}
    </div>
  );
}
